import './movie-card.scss';
import { Link } from 'react-router-dom';
import { FaPlay, FaHeart, FaFilm } from 'react-icons/fa';
import { IoClose } from 'react-icons/io5';
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { pushFavourite, removeFavourite } from '../../Redux/actions/FavouriteThunk';
import { favouriteService } from '../../Service/FavouriteService';
import { encryptId } from '../SecurityComponent/cryptoUtils';
import { useDispatch } from 'react-redux';
import { message } from 'antd';
import tmdbApi from '../../api/tmdbApi';

const POPUP_W = 300;

// Module-level cache — fetched once for all cards
let _vipCache = null;
let _vipFetch = null;
function getVipIds() {
    if (_vipCache) return Promise.resolve(_vipCache);
    if (!_vipFetch) {
        _vipFetch = fetch('/movies-featured-api.php?vipIds=1')
            .then(r => r.json())
            .then(d => { _vipCache = new Set((d.ids || []).map(Number)); return _vipCache; })
            .catch(() => { _vipFetch = null; return new Set(); });
    }
    return _vipFetch;
}

// Favorites cache — ONE request for all cards instead of per-card check
let _favIds = null;
let _favFetch = null;
function getFavIds() {
    if (!localStorage.getItem('accessToken')) return Promise.resolve(new Set());
    if (_favIds !== null) return Promise.resolve(_favIds);
    if (!_favFetch) {
        _favFetch = favouriteService.favorites()
            .then(res => {
                const raw = res?.data;
                const list = Array.isArray(raw) ? raw
                    : Array.isArray(raw?.content) ? raw.content
                    : [];
                _favIds = new Set(list.map(m => Number(m.id || m.movieId)));
                return _favIds;
            })
            .catch(() => { _favFetch = null; _favIds = new Set(); return _favIds; });
    }
    return _favFetch;
}

const MovieCard = ({ item, variant, trailerMode = false }) => {
    const poster   = item.poster_path;
    const backdrop = item.backdrop_path || item.poster_path;
    const dispatch  = useDispatch();
    const wrapRef   = useRef(null);
    const hideTimer  = useRef(null);
    const showTimer  = useRef(null);
    const [show, setShow]         = useState(false);
    const [posterError, setPosterError] = useState(false);
    const [popupStyle, setPopupStyle] = useState({});
    const [liked, setLiked]       = useState(false);
    const [showTrailer, setShowTrailer]     = useState(false);
    const [trailerKey, setTrailerKey]       = useState(null);   // YouTube video ID
    const [trailerGdrive, setTrailerGdrive] = useState(null);   // GDrive direct/embed URL
    const [trailerLoading, setTrailerLoading] = useState(false);
    const [isVip, setIsVip] = useState(!!item.restrictedAccess);

    const scheduleHide = () => {
        if (showTimer.current) clearTimeout(showTimer.current);
        hideTimer.current = setTimeout(() => setShow(false), 200);
    };
    const cancelHide = () => {
        if (hideTimer.current) clearTimeout(hideTimer.current);
    };
    const watchUrl = `/watch/${item.slug}/${encryptId(1)}`;

    useEffect(() => {
        if (!item?.id) return;
        getFavIds().then(ids => { if (ids.has(Number(item.id))) setLiked(true); });
        getVipIds().then(ids => { if (ids.has(Number(item.id))) setIsVip(true); });
    }, [item?.id]);

    useEffect(() => {
        const onScroll = () => setShow(false);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Close trailer modal with Escape key
    useEffect(() => {
        if (!showTrailer) return;
        const onKey = (e) => { if (e.key === 'Escape') closeTrailer(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [showTrailer]);

    const handleMouseEnter = () => {
        cancelHide();
        showTimer.current = setTimeout(() => {
            if (!wrapRef.current) return;
            const rect = wrapRef.current.getBoundingClientRect();

            let left = rect.left + rect.width / 2 - POPUP_W / 2;
            if (left < 8) left = 8;
            if (left + POPUP_W > window.innerWidth - 8) left = window.innerWidth - POPUP_W - 8;

            setPopupStyle({ position: 'fixed', top: rect.top, left, width: POPUP_W });
            setShow(true);
        }, 400);
    };

    const handleLike = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            if (!liked) {
                await dispatch(pushFavourite(item.id));
                if (_favIds) _favIds.add(Number(item.id));
                message.success('Đã thêm vào mục yêu thích!');
                setLiked(true);
            } else {
                await dispatch(removeFavourite(item.id));
                if (_favIds) _favIds.delete(Number(item.id));
                message.success('Đã xóa khỏi mục yêu thích!');
                setLiked(false);
            }
        } catch {
            message.error('Lỗi khi thêm yêu thích.');
        }
    };

    const closeTrailer = () => {
        setShowTrailer(false);
        setTrailerKey(null);
    };

    const extractYouTubeId = (url) => {
        if (!url) return null;
        const m = url.match(/(?:youtube\.com\/(?:watch\?.*v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
        return m ? m[1] : null;
    };

    const TMDB_KEY = '0623e0d232ae90274f2d77494ac4ce50';

    const tmdbVideos = async (type, id) => {
        const r = await fetch(`https://api.themoviedb.org/3/${type}/${id}/videos?api_key=${TMDB_KEY}`);
        return r.json();
    };

    const handleTrailer = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShow(false);
        setShowTrailer(true);
        setTrailerLoading(true);
        setTrailerKey(null);

        try {
            // 1. Dùng trailerUrl đã lưu trong DB (qua PHP API)
            if (item.id) {
                try {
                    const pr = await fetch(`/movies-trailer-api.php?movieId=${item.id}`);
                    const pd = await pr.json();
                    const phpKey = extractYouTubeId(pd?.data?.trailerUrl);
                    if (phpKey) { setTrailerKey(phpKey); return; }
                } catch {}
            }
            // fallback: check item.trailerUrl directly
            const stored = extractYouTubeId(item.trailerUrl);
            if (stored) { setTrailerKey(stored); return; }

            let key = null;

            // 2. Tìm trên TMDB theo tên phim (không dùng language=vi để tránh thiếu kết quả)
            const movieRes = await tmdbApi.search('movie', { params: { query: item.title } });
            if (movieRes.results?.length > 0) {
                const id = movieRes.results[0].id;
                const vids = await tmdbVideos('movie', id);
                const t = vids.results?.find(v => v.type === 'Trailer' && v.site === 'YouTube')
                         || vids.results?.find(v => v.site === 'YouTube');
                if (t) key = t.key;
            }

            // 3. Fallback: tìm TV series
            if (!key) {
                const tvRes = await tmdbApi.search('tv', { params: { query: item.title } });
                if (tvRes.results?.length > 0) {
                    const id = tvRes.results[0].id;
                    const vids = await tmdbVideos('tv', id);
                    const t = vids.results?.find(v => v.type === 'Trailer' && v.site === 'YouTube')
                             || vids.results?.find(v => v.site === 'YouTube');
                    if (t) key = t.key;
                }
            }

            setTrailerKey(key);
        } catch (err) {
            console.error('Trailer error:', err);
        } finally {
            setTrailerLoading(false);
        }
    };

    const displayQuality = item.quality || 'HD';

    const metaParts = [
        item.releaseYear,
        item.episodeCount > 1 ? `${item.episodeCount} tập` : null,
        item.duration
            ? `${Math.floor(item.duration / 60) > 0 ? `${Math.floor(item.duration / 60)}g` : ''}${item.duration % 60 > 0 ? `${item.duration % 60}p` : ''}`
            : null,
        item.country || null,
    ].filter(Boolean);

    const popup = show && ReactDOM.createPortal(
        <div
            className="mc-popup"
            style={{ ...popupStyle, position: 'fixed' }}
            onMouseEnter={cancelHide}
            onMouseLeave={scheduleHide}
        >
            <div className="mc-popup__backdrop" style={{ backgroundImage: `url(${backdrop})` }} />
            <div className="mc-popup__body">
                <div className="mc-popup__actions">
                    <button
                        className="mc-popup__play"
                        onClick={trailerMode
                            ? handleTrailer
                            : () => window.location.href = `/watch/${item.slug}/${encryptId(1)}`}
                    >
                        {trailerMode
                            ? <><FaFilm size={10} /> Xem trailer</>
                            : <><FaPlay size={10} /> Xem ngay</>}
                    </button>
                    <button
                        className="mc-popup__icon trailer"
                        onClick={handleTrailer}
                        title="Xem trailer"
                    >
                        <FaFilm size={12} />
                    </button>
                    <button
                        className={`mc-popup__icon${liked ? ' liked' : ''}`}
                        onClick={handleLike}
                    >
                        <FaHeart size={12} />
                    </button>
                </div>

                <p className="mc-popup__title">{item.title}</p>

                <div className="mc-popup__rating">
                    {item.averageRating > 0 && (
                        <span className="mc-star">⭐ {Number(item.averageRating).toFixed(1)}</span>
                    )}
                    {item.episodeCount > 1 && <span className="mc-badge">{item.episodeCount} tập</span>}
                    {item.latestEpisode && <span className="mc-badge new-ep">Tập {item.latestEpisode} mới</span>}
                    <span className="mc-badge hd">{displayQuality}</span>
                    {isVip && <span className="mc-badge vip">⭐ VIP</span>}
                </div>

                {metaParts.length > 0 && (
                    <p className="mc-popup__meta">{metaParts.join(' · ')}</p>
                )}

                {item.description ? (
                    <p className="mc-popup__desc"
                       dangerouslySetInnerHTML={{ __html: item.description.replace(/<[^>]+>/g, '') }} />
                ) : item.genres?.length > 0 && (
                    <p className="mc-popup__desc">{item.genres.map(g => g.name).join(' · ')}</p>
                )}
            </div>
        </div>,
        document.body
    );

    const trailerModal = showTrailer && ReactDOM.createPortal(
        <div className="mc-trailer-overlay" onClick={closeTrailer}>
            <div className="mc-trailer-box" onClick={e => e.stopPropagation()}>
                <div className="mc-trailer-box__header">
                    <span className="mc-trailer-box__label"><FaFilm size={13} /> Trailer</span>
                    <h3 className="mc-trailer-box__title">{item.title}</h3>
                    <button className="mc-trailer-box__close" onClick={closeTrailer}>
                        <IoClose size={20} />
                    </button>
                </div>
                {trailerLoading ? (
                    <div className="mc-trailer-box__state">
                        <div className="mc-trailer-spinner" />
                        <p>Đang tải trailer...</p>
                    </div>
                ) : trailerKey ? (
                    <div className="mc-trailer-box__player">
                        <iframe
                            src={`https://www.youtube.com/embed/${trailerKey}?autoplay=1&rel=0`}
                            allow="autoplay; encrypted-media; fullscreen"
                            allowFullScreen
                            title={`Trailer - ${item.title}`}
                        />
                    </div>
                ) : (
                    <div className="mc-trailer-box__state">
                        <p>Không tìm thấy trailer cho phim này.</p>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );

    return (
        <>
            <div
                className={`movie-card-wrapper${variant ? ` movie-card-wrapper--${variant}` : ''}`}
                ref={wrapRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={scheduleHide}
            >
                <Link
                    to={watchUrl}
                    onClick={trailerMode ? (e) => { e.preventDefault(); handleTrailer(e); } : undefined}
                >
                    <div className="movie-card">
                        {/* Poster thumbnail */}
                        <div className="movie-card__thumb">
                            {!posterError && poster
                                ? <img
                                    className="movie-card__poster-img"
                                    src={poster}
                                    alt={item.title}
                                    loading="lazy"
                                    decoding="async"
                                    onError={() => setPosterError(true)}
                                  />
                                : <div className="movie-card__no-img">🎬</div>
                            }

                            {/* Play icon on hover */}
                            <div className="movie-card__play-icon">▶</div>

                            {/* Top badges */}
                            <div className="movie-card__top-row">
                                {isVip && <span className="movie-card__vip">VIP</span>}
                                <span className={`movie-card__quality movie-card__quality--${displayQuality.replace(/\s/g,'').toLowerCase()}`}>
                                    {displayQuality}
                                </span>
                            </div>

                            {/* Episode / trailer badge */}
                            {trailerMode
                                ? <span className="movie-card__trailer-badge">▶ Trailer</span>
                                : item.latestEpisode && (
                                    <span className="movie-card__new-episode">Tập {item.latestEpisode}</span>
                                )
                            }
                        </div>
                    </div>

                    {/* Title bar — outside card, below poster */}
                    <div className="movie-card__bar">
                        <h3 className="movie-card-title">{item.title}</h3>
                    </div>
                </Link>
            </div>
            {popup}
            {trailerModal}
        </>
    );
};

export default MovieCard;
